import supplierRecallService from '../../../services/supplier/supplier-recall'
import { ENUM_PRODUCT_FIELD_DATA_TYPE } from '../../../misc/commons'
import DatePicker from '../../../components/noti/DatePicker'
import { ValidationProvider } from 'vee-validate'

export default {
    name: 'ProductManager',
    components: {
        DatePicker,
        ValidationProvider
    },
    props: {
        // Initial product data to populate
        value: {
            type: Array,
            default: () => []
        },
        // Product field metadata list
        productFieldMetaList: {
            type: Array,
            default: () => []
        },
        // Whether to show import button
        showImportButton: {
            type: Boolean,
            default: false
        },
        hideAddButton: {
            type: Boolean,
            default: false
        },
        disabled: {
            type: Boolean,
            default: false
        },
        communicationId: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            products: [],
            loading: false,
            producFieldMetaList: [],
            nextOrderId: 1
        }
    },
    computed: {
        dataTypeOptions() {
            return this.producFieldMetaList.map(field => ({
                name: field.name,
                code: field.name,
                type: field.type
            }))
        }
    },
    watch: {
        value: {
            handler(newValue) {
                if (newValue && newValue.length > 0) {
                    // Preserve existing products that have local changes
                    const updatedProducts = newValue.map((product, index) => {
                        const existingProduct = this.products[index]

                        // If we have an existing product with the same order, preserve its local state
                        if (existingProduct && existingProduct.order === (product.order || index + 1)) {
                            return {
                                ...product,
                                description: existingProduct.description || product.description || '',
                                quantity: existingProduct.quantity || product.quantity || '',
                                unitOfMeasure: existingProduct.unitOfMeasure || product.unitOfMeasure || '',
                                fields: existingProduct.fields || product.fields || [],
                                order: index + 1
                            }
                        }

                        return {
                            ...product,
                            quantity: product.quantity || '',
                            unitOfMeasure: product.unitOfMeasure || '',
                            order: index + 1
                        }
                    })
                    this.products = updatedProducts
                } else {
                    this.products = []
                }
                this.updateNextOrderId()
            },
            immediate: true,
            deep: true
        },
        productFieldMetaList: {
            handler(newValue) {
                this.producFieldMetaList = newValue || []
            },
            immediate: true
        }
    },
    methods: {
        addProduct() {
            const newProduct = {
                id: 0,
                description: '',
                quantity: '',
                unitOfMeasure: '',
                fields: [], // Start with no fields - progressive disclosure
                order: this.nextOrderId
            }
            this.products.push(newProduct)
            this.updateNextOrderId()
            // Emit events only for meaningful changes
            this.$emit('input', this.products)
            this.$emit('products-changed', this.products)
        },

        removeProduct(index) {
            if (this.products.length > 0) {
                this.products.splice(index, 1)
                // Reorder products
                this.products.forEach((product, idx) => {
                    product.order = idx + 1
                })
                this.updateNextOrderId()
                // Emit events only for meaningful changes
                this.$emit('input', this.products)
                this.$emit('products-changed', this.products)
            }
        },

        addNewField(productIndex) {
            if (this.products[productIndex]) {
                this.products[productIndex].fields.push({
                    field_name: '',
                    type: null,
                    required: false,
                    value: null
                })
                // Emit the updated products to keep parent in sync
                this.$emit('input', this.products)
                this.$emit('products-changed', this.products)
            }
        },

        removeField(productIndex, fieldIndex) {
            if (this.products[productIndex] && this.products[productIndex].fields && this.products[productIndex].fields.length > 0) {
                this.products[productIndex].fields.splice(fieldIndex, 1)
            }
        },

        addFieldToProduct(productIndex) {
            // Legacy method - redirect to addNewField for compatibility
            this.addNewField(productIndex)
        },

        handleFieldTypeChange(productIndex, fieldIndex, newFieldName) {
            if (this.products[productIndex] && this.products[productIndex].fields[fieldIndex]) {
                const field = this.products[productIndex].fields[fieldIndex]
                field.field_name = newFieldName

                if (newFieldName) {
                    // Update field type based on metadata
                    const fieldMeta = this.producFieldMetaList.find(f => f.name === newFieldName)
                    if (fieldMeta) {
                        field.type = fieldMeta.type
                        field.required = fieldMeta.required || false
                        // Reset value to appropriate default using the same pattern as step1-products
                        field.value = this.getDefaultValueForType(fieldMeta.type)
                    }
                } else {
                    field.type = null
                    field.value = null
                }
            }
        },

        getFieldType(fieldName) {
            const fieldMeta = this.producFieldMetaList.find(f => f.name === fieldName)
            return fieldMeta ? fieldMeta.type : ENUM_PRODUCT_FIELD_DATA_TYPE.TEXT
        },

        getDefaultValueForType(type) {
            switch (type) {
            case ENUM_PRODUCT_FIELD_DATA_TYPE.TEXT:
                return { valueText: '' }
            case ENUM_PRODUCT_FIELD_DATA_TYPE.NUMBER:
                return { valueNumber: '' }
            case ENUM_PRODUCT_FIELD_DATA_TYPE.DATE:
                return { valueDate: null }
            case ENUM_PRODUCT_FIELD_DATA_TYPE.LIST_SINGLE:
                return { valueListSingle: null }
            case ENUM_PRODUCT_FIELD_DATA_TYPE.LIST_MULTIPLE:
                return { valueListMultiple: [] }
            case ENUM_PRODUCT_FIELD_DATA_TYPE.YES_NO:
                return { valueYesNo: null }
            case ENUM_PRODUCT_FIELD_DATA_TYPE.TRUE_FALSE:
                return { valueTrueFalse: null }
            default:
                return { valueText: '' }
            }
        },

        updateNextOrderId() {
            if (this.products.length > 0) {
                this.nextOrderId = Math.max(...this.products.map(p => p.order || 0)) + 1
            } else {
                this.nextOrderId = 1
            }
        },

        showImportProductModal() {
            this.$emit('import-products')
        },

        // Validation method for parent components
        validate() {
            if (this.$refs.observer) {
                return this.$refs.observer.validate()
            }
            return Promise.resolve(true)
        },

        // Get formatted product data for submission
        getProductsData() {
            return this.products.map(product => ({
                ...product,
                fields: product.fields.map(field => ({
                    ...field,
                    value: this.getFieldValueForSubmission(field)
                }))
            }))
        },

        getFieldValueForSubmission(field) {
            if (!field.value) {
                return this.getDefaultValueForType(field.type)
            }
            return field.value
        },

        // Reset the component
        reset() {
            this.products = []
            this.nextOrderId = 1
        }
    }
}